import { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsivePie } from "@nivo/pie";
import ChartCardPopup from "./ChartCardPopup";
import { useNavigate } from "react-router-dom";
import { setSelectedReportViewId } from "../../slices/viewReportSlice";
import { useDispatch } from "react-redux";
import CustomLegend from "./charts/customLegend";
import html2canvas from "html2canvas";
import Tooltip from "../../components/ui/Tooltip";

// Centered label inside a donut chart
const CenteredMetric = ({
    centerX,
    centerY,
    label,
    pieTotalRecords,
    disabilitySnapshot,
}) => {
    const words = label.split(" ");
    const lineHeight = 1.4;
    const totalHeight = (words.length - 1) * lineHeight;

    return (
        <text
            x={centerX}
            y={centerY}
            textAnchor="middle"
            dominantBaseline="middle"
            style={{
                // fontSize: 12.8,
                fontWeight: 400,
                fill: "#525252",
            }}
        >
            {disabilitySnapshot ? (
                <>
                    <tspan x={centerX} style={{ fontSize: 25 }}>
                        {pieTotalRecords}
                    </tspan>
                    <tspan x={centerX} dy="2em" style={{ fontSize: 12.8 }}>
                        Claims
                    </tspan>
                </>
            ) : (
                words.map((word, i) => (
                    <tspan
                        key={`${word}-${i}`}
                        x={centerX}
                        dy={
                            i === 0
                                ? -totalHeight / 2 + "em"
                                : lineHeight + "em"
                        }
                    >
                        {word}
                    </tspan>
                ))
            )}
        </text>
    );
};
CenteredMetric.propTypes = {
    centerX: PropTypes.number,
    centerY: PropTypes.number,
    label: PropTypes.string,
};

// Custom component for centered metric in donut chart
const CustomCenteredMetric =
    ({ label, pieTotalRecords, disabilitySnapshot }) =>
    (props) => (
        <CenteredMetric
            {...props}
            label={label}
            pieTotalRecords={pieTotalRecords}
            disabilitySnapshot={disabilitySnapshot}
        />
    );

// Individual Donut chart container
const PieChartCenterText = ({
    options,
    label,
    pieTotalRecords,
    disabilitySnapshot,
}) => {
    return (
        <div className="donut-chart" style={{ width: "100%", height: 440 }}>
            <ResponsivePie
                {...options}
                layers={[
                    "arcs",
                    "arcLabels",
                    "arcLinkLabels",
                    "legends",
                    CustomCenteredMetric({
                        label,
                        pieTotalRecords,
                        disabilitySnapshot,
                    }),
                ]}
            />
        </div>
    );
};

PieChartCenterText.propTypes = {
    options: PropTypes.object.isRequired,
    label: PropTypes.string.isRequired,
};

const ChartCard = ({
    title,
    description,
    accessibilityText,
    selectedCompanyYourCompanyTo,
    variant,
    chartOptions,
    labels = [],
    isRotate = false,
    errorMessage,
    disabilitySnapshot,
    totalRecords,
    viewId,
    pieTotalRecords,
    reportDetails,
    comparisons,
    legendOffsetY,
    disclaimer,
    cacheKey,
    clientClaimCount,
    chartHeight,
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [showPopup, setShowPopup] = useState(false);
    const [selectedHyperLink, setSelectedHyperLink] = useState("");
    const sectionRef = useRef(null);

    const handleClick = async () => {
        try {
            if (!sectionRef.current) {
                console.error(
                    "Section card ref not found for screenshot capture."
                );
                return;
            }

            const canvas = await html2canvas(sectionRef.current, {
                backgroundColor: "#ffffff",
                useCORS: true,
                scale: 2,
            });

            const imageData = canvas.toDataURL("image/png");

            dispatch(setSelectedReportViewId(viewId));
            navigate(`/dashboard/detailed-report`, {
                state: {
                    viewId,
                    selectedView: reportDetails?.find(
                        (view) => view.ViewId === viewId
                    ),
                    chartScreenshot: imageData,
                    disclaimer,
                    cacheKey,
                },
            });
        } catch (error) {
            console.error("Error capturing screenshot:", error);
        }
    };

    const handleOpenModal = (value) => {
        setShowPopup(true);
        setSelectedHyperLink(value);
    };

    const handleCloseModal = () => {
        setShowPopup(false);
    };

    useEffect(() => {
        document.body.style.overflow = showPopup ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [showPopup]);

    const isIndustryPeers = (selectedCompanyYourCompanyTo ?? [])
        .map((item) => item.value)
        .includes("industryPeers");

    // --------------------------------------------------------------------
    // Resuable Helpers
    // --------------------------------------------------------------------

    const renderBarChart = () => {
        if (errorMessage) {
            return (
                <div className="d-flex no-data-message">
                    <div className="d-flex" style={{ gap: "8px" }}>
                        <i className="d-flex align-items-center fa-solid fa-circle-exclamation warning-color"></i>
                        <div
                            className="infobanner-text"
                            dangerouslySetInnerHTML={{ __html: errorMessage }}
                        ></div>
                    </div>
                </div>
            );
        }

        return (
            <div
                className="chart-container"
                style={{
                    height: chartHeight
                        ? chartHeight + 50
                        : isRotate
                          ? 682
                          : disabilitySnapshot
                            ? 400
                            : 450,
                    zIndex: 1,
                }}
            >
                <div
                    style={{
                        height: chartHeight
                            ? chartHeight
                            : !disabilitySnapshot
                              ? 400
                              : "100%",
                    }}
                >
                    <ResponsiveBar {...chartOptions} />
                </div>

                {!disabilitySnapshot && (
                    <CustomLegend
                        comparisons={comparisons}
                        legendOffsetY={legendOffsetY}
                        isRotate={isRotate}
                    />
                )}
            </div>
        );
    };

    const renderDonutChart = () => {
        if (errorMessage) {
            return (
                <div className="d-flex no-data-message">
                    <div className="d-flex" style={{ gap: "8px" }}>
                        <i className="d-flex align-items-center fa-solid fa-circle-exclamation warning-color"></i>
                        <div
                            className="infobanner-text"
                            dangerouslySetInnerHTML={{ __html: errorMessage }}
                        ></div>
                    </div>
                </div>
            );
        }

        return (
            <div className="donut-group">
                {Array.isArray(chartOptions) ? (
                    chartOptions.map((opts, idx) => {
                        const uniqueKey =
                            opts.id || opts.label || labels[idx] || idx;
                        return (
                            <PieChartCenterText
                                key={uniqueKey}
                                options={opts}
                                label={labels[idx] || ""}
                                pieTotalRecords={pieTotalRecords}
                                disabilitySnapshot={disabilitySnapshot}
                            />
                        );
                    })
                ) : (
                    <PieChartCenterText
                        options={chartOptions}
                        label={labels[0] || ""}
                        pieTotalRecords={pieTotalRecords}
                        disabilitySnapshot={disabilitySnapshot}
                    />
                )}
            </div>
        );
    };

    return (
        <div
            className={`section-card px-0 pt-0 ${disabilitySnapshot ? "mb-0" : ""}`}
        >
            <div style={{ padding: "24px 24px 0px 24px" }} ref={sectionRef}>
                <h3 className="chart-title">
                    {title}{" "}
                    {disabilitySnapshot && totalRecords && (
                        <span>
                            ({totalRecords.toLocaleString("en-US")} claims)
                        </span>
                    )}
                </h3>
                <p className="chart-subtitle">{description} </p>

                {!errorMessage && (
                    <>
                        {!disabilitySnapshot && (
                            <>
                                <div className="d-flex gap-2 dashboard-hyper-links">
                                    {isIndustryPeers && (
                                        <>
                                            <button
                                                type="button"
                                                style={{
                                                    border: "none",
                                                    backgroundColor: "#fff",
                                                }}
                                                className="hyper-links"
                                                onClick={() =>
                                                    handleOpenModal("peers")
                                                }
                                            >
                                                View industry peers
                                            </button>
                                            <div
                                                className="vertical-divider"
                                                style={{ margin: 0 }}
                                            />
                                        </>
                                    )}
                                    <button
                                        type="button"
                                        style={{
                                            border: "none",
                                            backgroundColor: "#fff",
                                        }}
                                        className="hyper-links"
                                        onClick={() =>
                                            handleOpenModal("policies")
                                        }
                                    >
                                        View your policies
                                    </button>
                                </div>
                                <div className="client-claim-count-info">
                                    <span className="client-claim-count-text">
                                        Client claim count:{" "}
                                        {clientClaimCount.toLocaleString(
                                            "en-US"
                                        )}
                                    </span>
                                    <Tooltip
                                        content={
                                            <>
                                                The total number of your
                                                organization&#39;s claims
                                                included in this chart, based on
                                                the selected filters and
                                                reporting period. Client
                                                percentages are calculated using
                                                this count.
                                            </>
                                        }
                                        tooltipId="client-claim-count-tooltip"
                                        tooltipClassName="reporting-dropdown-tooltip"
                                        arrowClassName="reportingTooltipArrow"
                                        portal
                                        icon
                                        iconAriaLabel="More information about client claim count"
                                        iconColor="#015294"
                                        iconButtonStyle={{
                                            background: "transparent",
                                            border: 0,
                                            padding: "0 0 0 4px",
                                        }}
                                    />
                                </div>
                            </>
                        )}
                    </>
                )}
                <div
                    className="chart-content"
                    style={{
                        height: chartHeight
                            ? chartHeight + 50
                            : disabilitySnapshot && !errorMessage
                              ? 370
                              : "auto",
                    }}
                >
                    {variant === "bar" && renderBarChart()}
                    {variant === "donut" && renderDonutChart()}
                </div>

                {showPopup && (
                    <div
                        className="chart-card-modal-overlay"
                        role="dialog"
                        aria-modal="true"
                        tabIndex={-1}
                        onClick={(e) => {
                            if (
                                e.target.classList.contains(
                                    "chart-card-modal-overlay"
                                )
                            ) {
                                handleCloseModal();
                            }
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Escape") {
                                handleCloseModal();
                            }
                        }}
                    >
                        <ChartCardPopup
                            selectedHyperLink={selectedHyperLink}
                            handleCloseModal={handleCloseModal}
                        />
                    </div>
                )}
            </div>

            {!errorMessage && disabilitySnapshot && (
                <span
                    className="a-self viewButton d-flex"
                    style={{
                        position: "relative",
                        zIndex: 2,
                        justifyContent: "flex-end",
                        paddingRight: "24px",
                        paddingTop: "24px",
                    }}
                >
                    <button
                        type="button"
                        className="d-flex button viewDetailButton"
                        onClick={handleClick}
                    >
                        View details
                        <i className="fa-solid fa-chevron-right ms-2"></i>
                    </button>
                </span>
            )}
        </div>
    );
};

ChartCard.propTypes = {
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    selectedCompanyYourCompanyTo: PropTypes.string,
    accessibilityText: PropTypes.string,
    variant: PropTypes.oneOf(["bar", "donut"]),
    chartOptions: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.arrayOf(PropTypes.object),
    ]),
    labels: PropTypes.arrayOf(PropTypes.string),
    isRotate: PropTypes.bool,
    errorMessage: PropTypes.string,
    disabilitySnapshot: PropTypes.bool,
    totalRecords: PropTypes.number,
    viewId: PropTypes.number,
    pieTotalRecords: PropTypes.number,
    reportDetails: PropTypes.array,
    comparisons: PropTypes.array,
    legendOffsetY: PropTypes.number,
    disclaimer: PropTypes.string,
    cacheKey: PropTypes.string,
    clientClaimCount: PropTypes.number,
    chartHeight: PropTypes.number,
};

export default ChartCard;
